import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { vendorService } from '../../../services/vendorService';
import { supabase } from '../../../lib/supabase';

const VendorRegistrationForm = ({ isOpen, onClose, onSuccess, projectId }) => {
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    businessCategory: '',
    yearsInBusiness: '',
    licenseNumber: '',
    website: '',
    serviceAreas: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const businessCategories = [
    { value: 'home_improvement', label: 'Home Improvement' },
    { value: 'construction', label: 'Construction' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'landscaping', label: 'Landscaping' },
    { value: 'roofing', label: 'Roofing' },
    { value: 'hvac', label: 'HVAC' },
    { value: 'flooring', label: 'Flooring' },
    { value: 'painting', label: 'Painting' },
    { value: 'appliances', label: 'Appliance Installation' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.businessName?.trim()) {
      newErrors.businessName = 'Business name is required';
    }

    if (!formData?.contactName?.trim()) {
      newErrors.contactName = 'Contact name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData?.businessCategory) {
      newErrors.businessCategory = 'Business category is required';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Create Supabase auth user
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.contactName,
            company_name: formData?.businessName,
          }
        }
      });

      if (authError) throw authError;

      const authUserId = authData?.user?.id;

      // Step 2: Create vendor row in Supabase, linked to auth user
      const newVendor = await vendorService?.create({
        name: formData?.contactName,
        email: formData?.email,
        phone: formData?.phone,
        companyName: formData?.businessName,
        website: formData?.website || null,
        categories: formData?.businessCategory ? [formData?.businessCategory] : [],
        bio: formData?.serviceAreas ? `Service areas: ${formData?.serviceAreas}` : null,
        logoUrl: null,
        authUserId,
      });

      // Step 3: Sign the vendor in so they have an active session
      const { error: signInError } = await supabase?.auth?.signInWithPassword({
        email: formData?.email,
        password: formData?.password,
      });

      if (signInError) {
        // Non-fatal: vendor row was created, session just won't be active
        console.warn('Auto sign-in after registration failed:', signInError?.message);
      }

      // Call success handler with the new vendor UUID and form data
      onSuccess?.({
        vendorId: newVendor?.id,
        businessName: formData?.businessName,
        contactName: formData?.contactName,
        email: formData?.email,
        phone: formData?.phone,
      });
    } catch (error) {
      console.error('Registration error:', error);
      setErrors(prev => ({ ...prev, submit: error?.message || 'Registration failed. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Join Stackd Vendor Base</h2>
            <p className="text-text-secondary mt-1">Create your account to access quote opportunities</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-10 w-10"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Information */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Business Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Business Name"
                  required
                  value={formData?.businessName}
                  onChange={(e) => handleInputChange('businessName', e?.target?.value)}
                  error={errors?.businessName}
                  placeholder="Your Company Name"
                />

                <Input
                  label="Contact Name"
                  required
                  value={formData?.contactName}
                  onChange={(e) => handleInputChange('contactName', e?.target?.value)}
                  error={errors?.contactName}
                  placeholder="John Smith"
                />

                <Input
                  label="Email Address"
                  type="email"
                  required
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  placeholder="contact@yourcompany.com"
                />

                <Input
                  label="Phone Number"
                  type="tel"
                  required
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  error={errors?.phone}
                  placeholder="(555) 123-4567"
                />

                <Select
                  label="Business Category"
                  required
                  value={formData?.businessCategory}
                  onChange={(value) => handleInputChange('businessCategory', value)}
                  error={errors?.businessCategory}
                  options={businessCategories}
                  placeholder="Select your primary service"
                />

                <Input
                  label="Years in Business"
                  type="number"
                  value={formData?.yearsInBusiness}
                  onChange={(e) => handleInputChange('yearsInBusiness', e?.target?.value)}
                  placeholder="5"
                  min="0"
                />

                <Input
                  label="License Number (Optional)"
                  value={formData?.licenseNumber}
                  onChange={(e) => handleInputChange('licenseNumber', e?.target?.value)}
                  placeholder="LIC123456"
                />

                <Input
                  label="Website (Optional)"
                  type="url"
                  value={formData?.website}
                  onChange={(e) => handleInputChange('website', e?.target?.value)}
                  placeholder="https://yourcompany.com"
                />
              </div>

              <div className="mt-4">
                <Input
                  label="Service Areas"
                  value={formData?.serviceAreas}
                  onChange={(e) => handleInputChange('serviceAreas', e?.target?.value)}
                  placeholder="San Francisco, Oakland, San Jose"
                  description="Enter the cities or regions you serve, separated by commas"
                />
              </div>
            </div>

            {/* Account Security */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Account Security</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Password"
                  type="password"
                  required
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                  error={errors?.password}
                  placeholder="Create a secure password"
                />

                <Input
                  label="Confirm Password"
                  type="password"
                  required
                  value={formData?.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                  error={errors?.confirmPassword}
                  placeholder="Repeat your password"
                />
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Shield" size={20} className="text-primary mt-1 flex-shrink-0" />
                <div className="text-sm text-text-secondary">
                  <p className="mb-2">
                    By creating an account, you agree to our Terms of Service and Privacy Policy. 
                    Your information will be used to match you with relevant quote opportunities.
                  </p>
                  <p>
                    • No setup fees or monthly charges
                  </p>
                  <p>
                    • Pay only when you win projects
                  </p>
                  <p>
                    • Cancel anytime
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            loading={isSubmitting}
            iconName="UserPlus"
            iconPosition="left"
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account & View Quote'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VendorRegistrationForm;